/**
 * ItemDetailSidebar — Right-column cards for the two-column Item detail layout.
 * Shows complementary info: pricing summary, inventory status, storefront,
 * classification, and variants.
 *
 * Mirrors the card design pattern from OrderDetailSidebar.
 */

import { Package, Tag, Warehouse, DollarSign, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { SectionHeader } from "./SectionHeader";
import { useItemPricingTable } from "@/features/inventory/api/pricingSummaryHooks";
import { formatPrice } from "@/lib/format/currency";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ItemDetailSidebarProps {
  formData: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Shared typography (matches OrderDetailSidebar)
// ---------------------------------------------------------------------------

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
      {children}
    </div>
  );
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words leading-relaxed">
      {children}
    </div>
  );
}

function EmptyValue() {
  return <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
}

// ---------------------------------------------------------------------------
// Card shell (same radius/shadow as OrderDetailSidebar)
// ---------------------------------------------------------------------------

function SidebarCard({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(CARD, "p-4", className)}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function StatusDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "inline-block h-2 w-2 rounded-full",
        active ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600",
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ItemDetailSidebar({ formData }: ItemDetailSidebarProps) {
  const isDisabled = formData.disabled === 1;
  const isStockItem = formData.is_stock_item === 1;
  const hasVariants = formData.has_variants === 1;

  // Standard selling rate lives in Item Price (price lists), NOT on Item.standard_rate
  // (which is usually blank). Read the primary price-list row — the same source the
  // main Pricing section uses — so this card doesn't show "—" for priced items.
  const itemCode = (formData?.name as string) || "";
  const pricingTable = useItemPricingTable(itemCode);
  const primaryRow = pricingTable.data?.rows?.[0] ?? null;
  const standardRate = primaryRow?.base_price ?? formData.standard_rate ?? null;
  const currency = primaryRow?.currency ?? "THB";
  const valuationRate = formData.valuation_rate;

  return (
    <div>
      {/* Header row — mirrors the left column's section header row. That row is
          `flex items-center justify-between` and is inflated to h-9 (36px) by its
          action button (e.g. "Translate"), so we match with min-h-9 to keep the
          first sidebar card's top edge aligned with the left column's first card. */}
      <div className="flex items-center justify-between min-h-9">
        <SectionHeader label="Overview" textSize="small" />
      </div>
      <div className="flex flex-col gap-3">
      {/* Status card */}
      <SidebarCard title="Status" icon={Package}>
        <div className="flex items-center gap-2 mb-3">
          <StatusDot active={!isDisabled} />
          <span className={cn(
            "text-sm font-medium",
            isDisabled ? "text-gray-500" : "text-green-700 dark:text-green-400",
          )}>
            {isDisabled ? "Disabled" : "Active"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel>Item Type</FieldLabel>
            <FieldValue>{formData.item_type || formData.stock_uom || <EmptyValue />}</FieldValue>
          </div>
          <div>
            <FieldLabel>UOM</FieldLabel>
            <FieldValue>{formData.stock_uom || <EmptyValue />}</FieldValue>
          </div>
        </div>
        {hasVariants && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-blue-50 dark:bg-blue-950/30 px-3 py-2">
            <Layers className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
              Template with variants
            </span>
          </div>
        )}
      </SidebarCard>

      {/* Pricing card */}
      <SidebarCard title="Pricing" icon={DollarSign}>
        <div className="space-y-3">
          <div>
            <FieldLabel>Standard Selling Rate</FieldLabel>
            <FieldValue>
              {standardRate != null && standardRate !== 0
                ? formatPrice(Number(standardRate), currency)
                : <EmptyValue />
              }
            </FieldValue>
          </div>
          <div>
            <FieldLabel>Valuation Rate</FieldLabel>
            <FieldValue>
              {valuationRate != null && valuationRate !== 0
                ? formatPrice(Number(valuationRate), currency)
                : <EmptyValue />
              }
            </FieldValue>
          </div>
        </div>
      </SidebarCard>

      {/* Classification card */}
      <SidebarCard title="Classification" icon={Tag}>
        <div className="space-y-3">
          <div>
            <FieldLabel>Item Group</FieldLabel>
            <FieldValue>{formData.item_group || <EmptyValue />}</FieldValue>
          </div>
          <div>
            <FieldLabel>Brand</FieldLabel>
            <FieldValue>{formData.brand || <EmptyValue />}</FieldValue>
          </div>
        </div>
      </SidebarCard>

      {/* Inventory card */}
      {isStockItem && (
        <SidebarCard title="Inventory" icon={Warehouse}>
          <div className="space-y-3">
            <div>
              <FieldLabel>Stock Item</FieldLabel>
              <div className="flex items-center gap-2">
                <StatusDot active={isStockItem} />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {isStockItem ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <div>
              <FieldLabel>Default Warehouse</FieldLabel>
              <FieldValue>
                {formData.default_warehouse || <EmptyValue />}
              </FieldValue>
            </div>
          </div>
        </SidebarCard>
      )}
      </div>
    </div>
  );
}
